import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Simple API URL configuration
const appEnvironment = (process.env.REACT_APP_Environment || '').trim().toLowerCase();
const API_URL = (
  appEnvironment === 'development'
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production
) || '';
const API_BASE_URL = API_URL.trim().replace(/\/?$/, '/');

/** Resolved API root with trailing slash (e.g. http://localhost:8800/). */
export const getApiBaseUrl = () => API_BASE_URL;

// Auth lives in localStorage so the same session is visible in every tab/window.
// (sessionStorage is isolated per tab, which made "open in new tab" look logged out.)
const secureStorage = {
  setToken: (token) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token_flag', 'true');
  },

  getToken: () => {
    return localStorage.getItem('auth_token');
  },

  setRefreshToken: (token) => {
    localStorage.setItem('refresh_token', token);
  },

  getRefreshToken: () => {
    return localStorage.getItem('refresh_token');
  },

  setUserData: (userData) => {
    localStorage.setItem('user_data', JSON.stringify(userData));
  },

  getUserData: () => {
    const data = localStorage.getItem('user_data');
    return data ? JSON.parse(data) : null;
  },

  clearAll: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token_flag');
    localStorage.removeItem('isExpired');
    localStorage.removeItem('pondstat_scoped_account');
    // Also clear legacy localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('token_flag');
    sessionStorage.removeItem('isExpired');
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('auth_token');
    return token !== null && token !== '';
  },
  
  // Migration helper
  migrateFromLocalStorage: () => {
    const oldToken = localStorage.getItem('token');
    const oldUser = localStorage.getItem('user');
    const oldRefresh = localStorage.getItem('refreshToken');
    
    if (oldToken) {
      secureStorage.setToken(oldToken);
      localStorage.removeItem('token');
    }
    if (oldUser) {
      secureStorage.setUserData(JSON.parse(oldUser));
      localStorage.removeItem('user');
    }
    if (oldRefresh) {
      secureStorage.setRefreshToken(oldRefresh);
      localStorage.removeItem('refreshToken');
    }
  },

  // One-time: older builds stored auth in sessionStorage (not shared with new tabs).
  migrateFromSessionStorage: () => {
    if (localStorage.getItem('auth_token')) return;
    const token = sessionStorage.getItem('auth_token');
    if (!token) return;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token_flag', 'true');
    const refresh = sessionStorage.getItem('refresh_token');
    if (refresh) localStorage.setItem('refresh_token', refresh);
    const userData = sessionStorage.getItem('user_data');
    if (userData) localStorage.setItem('user_data', userData);
    const expired = sessionStorage.getItem('isExpired');
    if (expired != null) localStorage.setItem('isExpired', expired);
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('token_flag');
    sessionStorage.removeItem('isExpired');
  }
};

// Auto-migrate on load
if (typeof window !== 'undefined') {
  secureStorage.migrateFromLocalStorage();
  secureStorage.migrateFromSessionStorage();
}

// Create a simple axios instance for other components that need it
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

let sessionRedirectInProgress = false;

const redirectToLogin = () => {
  if (sessionRedirectInProgress) return;
  sessionRedirectInProgress = true;
  secureStorage.clearAll();
  try {
    localStorage.removeItem("pondstat_scoped_account");
  } catch {
    /* ignore */
  }
  const loginPath = '/user/login';
  if (window.location.pathname !== loginPath) {
    window.location.replace(loginPath);
  }
};

/** Pending refresh so concurrent 401s share one RefreshToken call. */
let refreshInFlight = null;

/** Refresh access token when it is close to expiring (default: 30 minutes left). */
const TOKEN_REFRESH_BUFFER_SEC = Number(process.env.REACT_APP_TOKEN_REFRESH_BUFFER_SEC || 30 * 60);

const refreshAccessToken = async () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    const refreshToken = secureStorage.getRefreshToken();
    if (!refreshToken) {
      secureStorage.clearAll();
      redirectToLogin();
      return null;
    }
    try {
      const base = API_BASE_URL.replace(/\/$/, '');
      const userData = secureStorage.getUserData();
      let isSuper = !!(userData?.isSuperUser || userData?.IsSuperUser);
      let isCompany = !!(userData?.isCompanyManager || userData?.IsCompanyManager);
      if (!isSuper && !isCompany) {
        try {
          const access = secureStorage.getToken();
          if (access) {
            const decoded = jwtDecode(access);
            isSuper = !!decoded.isSuperUser;
            isCompany = !!decoded.isCompanyManager;
          }
        } catch {
          /* ignore */
        }
      }
      const refreshPath = isSuper
        ? "api/SuperUser/RefreshToken"
        : isCompany
          ? "api/company/RefreshToken"
          : "api/User/RefreshToken";
      const response = await axios.post(`${base}/${refreshPath}`, { refreshToken });
      const newAccessToken = response.data?.token;
      if (!newAccessToken) {
        throw new Error('No token in refresh response');
      }
      secureStorage.setToken(newAccessToken);
      if (isCompany && response.data?.User) {
        const prev = secureStorage.getUserData() || {};
        secureStorage.setUserData({ ...prev, ...response.data.User });
      }
      return newAccessToken;
    } catch (_) {
      secureStorage.clearAll();
      redirectToLogin();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const ensureFreshAccessToken = async () => {
  const token = secureStorage.getToken();
  if (!token) return null;

  const refreshToken = secureStorage.getRefreshToken();
  if (!refreshToken) return token;

  try {
    const decoded = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp - now > TOKEN_REFRESH_BUFFER_SEC) {
      return token;
    }
  } catch {
    /* fall through to refresh */
  }

  return refreshAccessToken();
};

/** True when the API rejected the JWT itself (not a business-rule forbidden/unauthorized). */
export const isJwtAuthFailure = (error) => {
  const msg = error?.response?.data?.message;
  return (
    msg === 'Invalid or expired token' ||
    msg === 'No token provided'
  );
};

/**
 * Legacy page 401 handlers. Company/Super are never force-logged-out (scoped work).
 * Returns { loggedOut, message } so callers can toast `message`.
 */
export const handleLegacySessionExpired = (
  error,
  fallbackMessage = 'Session expired. Please log in again.'
) => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    fallbackMessage;
  if (isCompanyManagerUser() || isSuperUserSession()) {
    return { loggedOut: false, message };
  }
  handleSessionExpired();
  return { loggedOut: true, message };
};

/** Clear session and send the browser to login (used by legacy 401 handlers). */
export const handleSessionExpired = () => {
  // Company/Super scoped sessions must not be force-logged-out on business 401s.
  try {
    const userData = secureStorage.getUserData();
    if (
      userData?.isCompanyManager ||
      userData?.IsCompanyManager ||
      userData?.isSuperUser ||
      userData?.IsSuperUser
    ) {
      return;
    }
    const token = secureStorage.getToken();
    if (token) {
      const decoded = jwtDecode(token);
      if (decoded.isCompanyManager || decoded.isSuperUser) return;
    }
  } catch {
    /* fall through */
  }
  redirectToLogin();
};

// Request Interceptor - Attach token; refresh proactively while the user is active
axiosInstance.interceptors.request.use(async (config) => {
  const token = await ensureFreshAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor - Handle expired tokens and refresh
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const closedCode = error.response?.data?.code;
    if (
      error.response?.status === 403 &&
      (closedCode === "ACCOUNT_CLOSED" ||
        /account has been deleted/i.test(String(error.response?.data?.message || "")))
    ) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (!isJwtAuthFailure(error)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      secureStorage.clearAll();
      redirectToLogin();
      // Reject (don't hang) so any awaiting UI settles instead of freezing on a
      // spinner forever while the login redirect happens.
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const newAccessToken = await refreshAccessToken();
    if (!newAccessToken) {
      return Promise.reject(error);
    }

    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return axiosInstance(originalRequest);
  }
);

export default axiosInstance;

export const getUserDetails = () => {
  try {
    let userData = secureStorage.getUserData();

    if (!userData) {
      // Fallback to localStorage for migration
      const userString = localStorage.getItem("user");
      if (userString) {
        userData = JSON.parse(userString);
        secureStorage.setUserData(userData);
        localStorage.removeItem("user");
      }
    }

    if (!userData) return {};

    // When Company/Super are working on an account, expose that fisher as the
    // effective org so Dashboard / Staff / Ponds pages behave like an owner.
    try {
      const isCompany =
        userData.isCompanyManager === true ||
        userData.IsCompanyManager === true ||
        userData.isCompanyManager === 1;
      const isSuper =
        userData.isSuperUser === true ||
        userData.IsSuperUser === true ||
        userData.isSuperUser === 1;
      if (isCompany || isSuper) {
        const raw = localStorage.getItem("pondstat_scoped_account");
        if (raw) {
          const scoped = JSON.parse(raw);
          const scopedId = scoped?.id != null ? Number(scoped.id) : NaN;
          if (!Number.isNaN(scopedId)) {
            return {
              ...userData,
              FisherID: scopedId,
              IsAdmin: true,
              IsFisherOwner: true,
              hasScientistPondAccess: true,
              canEditSamplingFishData: true,
              canWriteOperationalOnPonds: true,
              scopedAccountId: scopedId,
              scopedAccountName: scoped.name || null,
            };
          }
        }
      }
    } catch {
      /* ignore scope overlay */
    }

    return userData;
  } catch (error) {
    return {};
  }
};

/** Resolved staff primary key for API paths (stored user or JWT). */
export const getStaffId = (u) => {
  const d = u || getUserDetails();
  if (d.id != null && d.id !== "") return d.id;
  if (d.staffId != null && d.staffId !== "") return d.staffId;
  try {
    const token = secureStorage.getToken();
    if (token) {
      const decoded = jwtDecode(token);
      return decoded.staffId ?? decoded.id ?? null;
    }
  } catch {
    /* ignore */
  }
  return null;
};

/** Feed/fertilization, stocking edits, global fish-type catalog, etc. */
export const hasScientistPondUIAccess = (u) => {
  if (isCompanyManagerUser(u) || isSuperUserSession(u)) return true;
  const d = u || getUserDetails();
  if (d.hasScientistPondAccess != null) return !!d.hasScientistPondAccess;
  return !!(d.IsAdmin || d.IsFisherOwner);
};

/** Create/delete ponds (fisher-wide), staff list, pond-detail list API branch, etc. */
export const isFisherWideUser = (u) => {
  const d = u || getUserDetails();
  return !!(d.IsAdmin || d.IsFisherOwner);
};

/** Sampling fish add/edit flows (angler included when assigned to ponds). */
export const canEditSamplingFishData = (u) => {
  if (isCompanyManagerUser(u) || isSuperUserSession(u)) return true;
  const d = u || getUserDetails();
  if (d.IsAdmin || d.IsFisherOwner) return true;
  if (d.hasAnyPondAssignment) return true;
  if (d.canEditSamplingFishData != null) return !!d.canEditSamplingFishData;
  return false;
};

/** Pond detail (sampling session) create/update — includes anglers on assigned ponds. */
export const canWriteOperationalPonds = (u) => {
  if (isCompanyManagerUser(u) || isSuperUserSession(u)) return true;
  const d = u || getUserDetails();
  if (d.IsAdmin || d.IsFisherOwner) return true;
  if (d.hasAnyPondAssignment) return true;
  if (d.canEditSamplingFishData) return true;
  if (d.canWriteOperationalOnPonds != null) return !!d.canWriteOperationalOnPonds;
  return false;
};

/** Delete sampling sessions (scientist / owner — not anglers). */
export const canDeletePondDetail = (u) => {
  const d = u || getUserDetails();
  if (d.IsAdmin || d.IsFisherOwner) return true;
  if (d.hasScientistPondAccess != null) return !!d.hasScientistPondAccess;
  return false;
};

export const isAnglerOnlyUser = (u) => {
  const d = u || getUserDetails();
  return !!d.isAnglerOnly;
};

/** True when this login is the fisher owner (team management). Handles 0/1 from MySQL and legacy sessions missing the flag until refreshed. */
export const isFisherOwnerUser = (u) => {
  const d = u || getUserDetails();
  const v = d.IsFisherOwner;
  if (v === true || v === 1 || v === "1") return true;
  if (v === false || v === 0 || v === "0") return false;
  return false;
};

/** True when this login is a Pond Management Company manager. */
export const isCompanyManagerUser = (u) => {
  const d = u || getUserDetails();
  if (d.isCompanyManager === true || d.IsCompanyManager === true || d.isCompanyManager === 1) {
    return true;
  }
  try {
    const token = secureStorage.getToken();
    if (token) {
      const decoded = jwtDecode(token);
      return !!(decoded.isCompanyManager);
    }
  } catch {
    /* ignore */
  }
  return false;
};

/** True when this AdminPanel session is a platform Super Admin (JWT/user flag). */
export const isSuperUserSession = (u) => {
  const d = u || getUserDetails();
  if (d.isSuperUser === true || d.IsSuperUser === true || d.isSuperUser === 1) {
    return true;
  }
  try {
    const token = secureStorage.getToken();
    if (token) {
      const decoded = jwtDecode(token);
      return !!(decoded.isSuperUser);
    }
  } catch {
    /* ignore */
  }
  return false;
};

/** True when the home Fisher is billed by a Pond Management Company. */
export const isCompanyManagedBillingUser = (u) => {
  const d = u || getUserDetails();
  return !!(
    d.isCompanyManagedBilling ||
    d.ManagedByCompanyID ||
    (d.subscription && d.subscription.ManagedByCompanyID)
  );
};

/**
 * Sampling-only AdminPanel: company-managed owners who self-login.
 * Company managers and Super keep the full UI (scope only filters data).
 */
export const isCompanyManagedSamplingOnlyUser = (u) => {
  const d = u || getUserDetails();
  if (isCompanyManagerUser(d) || isSuperUserSession(d)) return false;
  return isCompanyManagedBillingUser(d);
};

/** True when this login is an independent (global) scientist account. */
export const isIndependentScientistUser = (u) => {
  if (isCompanyManagerUser(u) || isSuperUserSession(u)) return false;
  const d = u || getUserDetails();
  if (
    d.isIndependentScientist ||
    d.IsIndependentScientist === true ||
    d.IsIndependentScientist === 1 ||
    d.IsIndependentScientist === "1"
  ) {
    return true;
  }
  const noHomeFisher = d.FisherID == null || d.FisherID === "";
  const notOrgAdmin = !d.IsAdmin && !d.IsFisherOwner;
  return !!(noHomeFisher && notOrgAdmin);
};

/** Merge fields into stored user (e.g. set IsFisherOwner after verifying via GetStaff / team list). */
export const mergeUserDetails = (partial) => {
  const cur = getUserDetails() || {};
  secureStorage.setUserData({ ...cur, ...partial });
};

export const CheckUserExpiry = async () => {
  const userData = getUserDetails();
  if (isCompanyManagerUser(userData) || isSuperUserSession(userData)) {
    localStorage.setItem("isExpired", "false");
    return true;
  }
  if (!userData || (!userData.id && !userData.FisherID && !isIndependentScientistUser(userData))) {
    return null;
  }

  if (userData.skipSubscriptionGate || isIndependentScientistUser(userData)) {
    localStorage.setItem("isExpired", "false");
    return true;
  }

  try {
    const response = await axiosInstance.get("api/User/AccessStatus");
    const isExpired = !!response.data?.isExpired;
    localStorage.setItem("isExpired", isExpired.toString());
    mergeUserDetails({
      skipSubscriptionGate: !!response.data?.skipSubscriptionGate,
      isIndependentScientist: !!response.data?.isIndependentScientist,
      requiresSubscriptionRenewal: !!response.data?.requiresSubscriptionRenewal,
      isCompanyManagedBilling: !!response.data?.isCompanyManagedBilling,
      ManagedByCompanyID: response.data?.ManagedByCompanyID ?? null,
      hasAnyPondAssignment: response.data?.hasAnyPondAssignment,
      hasScientistPondAccess: response.data?.hasScientistPondAccess,
      isAnglerOnly: response.data?.isAnglerOnly,
      canEditSamplingFishData: response.data?.canEditSamplingFishData,
      canWriteOperationalOnPonds: response.data?.canWriteOperationalOnPonds,
      subscription: response.data?.subscription ?? null,
      PackageID: response.data?.subscription?.PackageID ?? null,
      packageName: response.data?.subscription?.packageName ?? null,
      isTrialPeriod: response.data?.subscription?.isTrialPeriod ?? false,
      LicensedPondCount: response.data?.subscription?.LicensedPondCount ?? null,
      isSubscriptionActive: response.data?.subscription?.isSubscriptionActive ?? null,
      hasSoftwareAccess: response.data?.subscription?.hasSoftwareAccess ?? null,
      subscriptionEndDate: response.data?.subscription?.EndDate ?? null,
    });
    return !isExpired;
  } catch (error) {
    if (userData.FisherID) {
      try {
        const response = await axiosInstance.get(`api/fisher/GetFisher?id=${userData.FisherID}`);
        const EndDate = response.data.EndDate;
        if (!EndDate) return false;
        const expiryDate = new Date(EndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expiryDate.setHours(0, 0, 0, 0);
        const isExpired = today > expiryDate;
        localStorage.setItem("isExpired", isExpired.toString());
        return !isExpired;
      } catch {
        return false;
      }
    }
    return false;
  }
};

export const checkTokenValidity = async () => {
  const token = secureStorage.getToken();
  if (!token) {
    redirectToLogin();
    return false;
  }

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const secondsLeft = decodedToken.exp ? decodedToken.exp - currentTime : TOKEN_REFRESH_BUFFER_SEC + 1;

    if (secondsLeft <= TOKEN_REFRESH_BUFFER_SEC) {
      const newToken = await refreshAccessToken();
      return !!newToken;
    }

    return true;
  } catch (error) {
    const newToken = await refreshAccessToken();
    return !!newToken;
  }
};

// Additional utility functions for secure token management
export const setAuthTokens = (accessToken, refreshToken, userData) => {
  secureStorage.setToken(accessToken);
  if (refreshToken) {
    secureStorage.setRefreshToken(refreshToken);
  }
  if (userData) {
    secureStorage.setUserData(userData);
  }
};

export const clearAuthData = () => {
  secureStorage.clearAll();
  try {
    localStorage.removeItem("pondstat_scoped_account");
  } catch {
    /* ignore */
  }
};

export const isAuthenticated = () => {
  return secureStorage.isAuthenticated();
};